import { BulkActionBar } from '@/components/bulkQueue/BulkActionBar';
import { BulkCancelVisit } from '@/components/visits/dialogs/BulkCancelVisits/BulkCancelVisitsDialog';
import { EntrySource } from '@/components/visits/dialogs/ReassignVisits/v3/tracking/entrySource';
import { DialogType, useDialog } from '@/dialogs/DialogManager';
import { Button } from '@mui/material';
import { FC, useCallback } from 'react';

const pluralizeVisits = (count: number) => `${count} Visit${count !== 1 ? 's' : ''}`;

type BulkVisitsActionBarProps = {
    /** Number of currently selected rows; drives bar visibility. */
    selectedCount: number;
    /** The cancellable subset, mapped to the cancel dialog payload. */
    cancellableVisits?: BulkCancelVisit[];
    /** Show the Cancel button. Renders whenever true and is disabled at 0. */
    showCancel: boolean;
    /** Show the Reassign button. Like Cancel, it renders whenever true and is disabled at 0. */
    showReassign: boolean;
    /** The reassignable subset + entry source, opened when the Reassign button is clicked. */
    reassign?: { visitIds: string[]; entrySource?: EntrySource };
};

/**
 * Shared multi-selection action header used by every bulk-enabled visits table
 * (All Visits visit-state tabs, Needs Clinician/Service/Date queues). Owning the
 * bar, buttons, count formatting, and dialog dispatch here keeps the header
 * styling identical across surfaces — see FE-566.
 */
export const BulkVisitsActionBar: FC<BulkVisitsActionBarProps> = ({
    selectedCount,
    cancellableVisits = [],
    showCancel,
    showReassign,
    reassign,
}) => {
    const { openDialog } = useDialog();
    const reassignableCount = reassign?.visitIds.length ?? 0;

    const handleReassign = useCallback(() => {
        if (!reassign) {
            return;
        }
        openDialog(DialogType.ReassignVisits, {
            visitsToReassign: reassign.visitIds,
            cliniciansToReassign: [],
            entrySource: reassign.entrySource,
        });
    }, [openDialog, reassign]);

    const handleCancel = useCallback(() => {
        openDialog(DialogType.BulkCancelVisits, { visits: cancellableVisits });
    }, [openDialog, cancellableVisits]);

    return (
        <BulkActionBar visible={selectedCount > 0}>
            {showReassign && (
                <Button
                    variant='contained'
                    size='small'
                    disabled={reassignableCount === 0}
                    onClick={handleReassign}
                >
                    Reassign {pluralizeVisits(reassignableCount)}
                </Button>
            )}
            {showCancel && (
                <Button
                    variant='outlined'
                    size='small'
                    color='error'
                    disabled={cancellableVisits.length === 0}
                    onClick={handleCancel}
                >
                    Cancel {pluralizeVisits(cancellableVisits.length)}
                </Button>
            )}
        </BulkActionBar>
    );
};
